import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClerk } from "@clerk/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { PageHead } from "@/components/Brand";
import { errMsg, useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { ProfileFields, emptyProfile } from "@/components/ProfileFields";
import { SITE_LINKS } from "@/lib/content";
import { nextForIntent } from "@/pages/Journey";

export default function ProfileSetup() {
  const { me, refresh } = useMe();
  const api = useApi();
  const nav = useNavigate();
  const { signOut } = useClerk();
  const [form, setForm] = useState({ ...emptyProfile, ...(me?.profile || {}) });
  const [busy, setBusy] = useState(false);
  const [under, setUnder] = useState(false);
  const [agree, setAgree] = useState(!!me?.consented);
  const migrated = me?.migrated && !me?.profile_complete;

  const submit = async (e) => {
    e.preventDefault();
    if (form.age_range === "Under 18") {
      setBusy(true);
      try { await api.delete("/account"); } catch {}
      setUnder(true);
      setTimeout(() => signOut({ redirectUrl: "/start" }), 6000);
      return;
    }
    setBusy(true);
    try {
      await api.put("/me/profile", { ...form, agree_terms: agree });
      const fresh = await refresh();
      const intent = localStorage.getItem("ahu_intent");
      const next = nextForIntent(intent);
      if (next) return nav(next);
      if (intent === "free") localStorage.removeItem("ahu_intent");
      nav(fresh?.baskets ? "/" : "/baskets");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (under) {
    return (
      <AppShell nav={false}>
        <div className="ahu-card p-8 fade-in" data-testid="underage-message">
          <h1 className="text-2xl">AHU is for adults 18 and over</h1>
          <p className="mt-4 text-ink-soft">Thank you for your interest. We haven't kept any of your details. If you need someone to talk to, free call or text 1737 any time.</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell nav={false}>
      {migrated
        ? <PageHead eyebrow="Welcome to the new AHU" title="Check your details" sub="We've brought your details across from the first AHU app. Add your area, check everything's right, and agree to the terms to continue." />
        : <PageHead eyebrow="Your profile" title="Tell us a little about you" sub="This helps AHU speak to you properly. It stays private." />}
      <form onSubmit={submit} className="ahu-card p-6 sm:p-8" data-testid="profile-setup-form">
        <ProfileFields form={form} setForm={setForm} />
        {!me?.consented && (
          <label className="mt-7 flex items-start gap-3 cursor-pointer" data-testid="terms-consent">
            <input type="checkbox" className="mt-1 h-4 w-4 accent-[#424741]" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="terms-consent-checkbox" />
            <span className="text-[15px] text-ink">I agree to the{" "}
              <a href={SITE_LINKS.terms.href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">Terms of Use</a> and{" "}
              <a href={SITE_LINKS.privacy.href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">Privacy Policy</a>.</span>
          </label>
        )}
        <button type="submit" disabled={busy || !agree} className="btn-charcoal mt-8 w-full sm:w-auto" data-testid="profile-continue-btn">{busy ? "Saving" : "Continue"}</button>
      </form>
    </AppShell>
  );
}
